"use client";

import { useMemo, useState } from "react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import {
    composerDevis,
    euros,
    natureDuVersement,
    obligation,
    recapitulatif,
    totaux,
    valider,
} from "@/lib/tools/quotation";
import { MENTION_FRANCHISE_CIBS, mentionFranchise } from "@/lib/tools/vatMentions";
import { documentEnTexte, nomDuFichier } from "@/lib/tools/documentText";
import { telechargerDocument } from "@/lib/tools/documentPdf";
import { ApercuDocument } from "./DocumentPaper";
import { PadSignature, useSignature } from "./SignaturePad";
import {
    Avancer,
    Champ,
    Choix,
    Ecran,
    Liste,
    Recommencer,
    Resultat,
    STYLE_CHAMP,
    Segments,
    remplir,
    useParcours,
} from "./Journey";
import { useDossierOutil, useSignalerIdentite } from "./ToolsDossier";

/**
 * Le devis : ce qu'il doit porter, et ce que sa rédaction engage.
 *
 * LA FACTURATION ÉLECTRONIQUE NE LE CONCERNE PAS, et c'est la première chose
 * que la page dit, parce que c'est la question que tout le monde se pose. La
 * réforme porte sur les factures. Un devis reste une offre de contrat, qui
 * circule en PDF, par courriel ou sur papier.
 *
 * Ce qu'elle change tient en une ligne : la facture qui suivra devra porter le
 * SIREN du client professionnel. Le parcours le demande donc dès le devis, au
 * moment où le client est motivé pour le donner.
 *
 * TROIS PIÈGES DÉSAMORCÉS DANS LE DOCUMENT, tous chers :
 *   - une TVA écrite en franchise est due du seul fait d'être écrite ;
 *   - sans le mot « acompte », la somme versée par un particulier est réputée
 *     être des arrhes, et la commande devient annulable ;
 *   - le pavé du client reste vide : lui remettre un devis déjà paraphé en son
 *     nom n'aurait aucune valeur.
 */

const L = {
    fr: {
        phases: ["Votre client", "Le devis", "Les conditions"],
        retour: "Retour",
        prive: "Tout se fait sur votre appareil : le devis s'y fabrique, personne ne reçoit vos réponses, pas même nous, et tout disparaît avec l'onglet.",

        clientTitre: "À qui adressez-vous ce devis ?",
        clientAide: "Cette réponse décide de trois choses : si le devis est imposé, si vous demandez un SIREN, et ce que vaudra la somme versée à la commande.",
        clientPro: "Une entreprise",
        clientProDetail: "Société, association, administration",
        clientParticulier: "Un particulier",
        clientParticulierDetail: "Un consommateur, pour ses besoins privés",

        activiteTitre: "Votre activité figure-t-elle dans cette liste ?",
        activiteAide: "Un devis écrit y est imposé par la réglementation, et ne pas le remettre expose à une amende administrative.",
        batiment: "Bâtiment et dépannage",
        batimentDetail: "Plomberie, serrurerie, électricité, peinture, couverture",
        demenagement: "Déménagement",
        demenagementDetail: "Transport et manutention de mobilier",
        locationVoiture: "Location de voiture",
        locationVoitureDetail: "Véhicules de tourisme et utilitaires",
        servicesALaPersonne: "Services à la personne",
        servicesALaPersonneDetail: "Ménage, garde d'enfants, aide à domicile",
        optique: "Optique médicale",
        optiqueDetail: "Lunettes et lentilles correctrices",
        audioprothese: "Appareillage auditif",
        audioprotheseDetail: "Prothèses auditives et accessoires",
        chirurgieEsthetique: "Chirurgie esthétique",
        chirurgieEsthetiqueDetail: "Actes à visée exclusivement esthétique",
        funeraire: "Prestations funéraires",
        funeraireDetail: "Obsèques, transport et inhumation",
        aucune: "Aucune de ces activités",
        aucuneDetail: "Le devis reste alors libre, et fortement conseillé",
        listeComplete: "La liste détaillée, activité par activité, est tenue à jour par Service Public Entreprendre : elle descend à des seuils de montant que cette page ne reprend pas.",

        partiesTitre: "Qui établit le devis, et pour qui",
        partiesAide: "Ces coordonnées figurent en tête du devis. Écrivez les adresses comme sur une enveloppe, ligne par ligne.",
        champPrenoms: "Vos prénoms",
        champNom: "Votre nom",
        champAdresse: "Votre adresse",
        aideAdresse: "Rue, puis code postal et commune, sur des lignes séparées.",
        champSiret: "Votre SIRET",
        aideSiret: "Quatorze chiffres, sans espaces.",
        champNomClient: "Nom du client",
        aideNomClient: "La dénomination exacte de l'entreprise, ou le nom de la personne.",
        champAdresseClient: "Son adresse",
        champSirenClient: "Son SIREN",
        aideSirenClient: "Neuf chiffres. Facultatif aujourd'hui, mais votre facture devra le porter à partir du 1er septembre 2027 : autant le demander maintenant.",
        champLieuExecution: "Lieu d'exécution",
        aideLieuExecution: "L'adresse du chantier, ou celle où la prestation sera rendue.",

        prestationsTitre: "Ce que vous chiffrez",
        prestationsAide: "Le décompte détaillé fait partie des mentions obligatoires : chaque prestation et chaque fourniture, en quantité et en prix unitaire.",
        champObjet: "Objet du devis",
        aideObjet: "En quelques mots : « pose d'une bibliothèque sur mesure ».",
        colonneDesignation: "Désignation",
        colonneQuantite: "Quantité",
        colonnePrix: "Prix unitaire",
        placeholderDesignation: "Main-d'oeuvre de pose",
        ajouterLigne: "Ajouter une ligne",
        retirerLigne: "Retirer cette ligne",
        choixManquant: "Choisissez une réponse.",
        totalProvisoire: "Total",
        aideLignes: "Les heures de main-d'oeuvre et les frais de déplacement se posent comme les fournitures, chacun sur sa ligne.",

        prixTitre: "La TVA et l'acompte",
        prixAide: "Chacune de ces deux réponses cache un piège : une TVA écrite par erreur devient due, et une somme versée à la commande ne vaut pas la même chose selon le mot employé.",
        champTva: "Votre situation au regard de la TVA",
        franchise: "En franchise",
        redevable: "Redevable",
        franchiseNote: "Votre devis ne portera aucune ligne de taxe, seulement la mention « {mention} ». Le prix écrit est exactement le prix payé.",
        franchiseDatee: "La mention suit la date du devis : « TVA non applicable, article 293 B du CGI » jusqu'au 31 décembre 2026, « TVA non applicable, article L. 233-3 du CIBS » à partir du 1er janvier 2027, jour où les règles de TVA changent de code.",
        redevableNote: "Toute TVA mentionnée devient due du seul fait de figurer sur le document. Vérifiez votre taux avant de l'écrire.",
        champTauxTva: "Votre taux de TVA",
        aideTauxTva: "En pourcentage.",
        champAcompte: "Demandez-vous un acompte à la commande ?",
        oui: "Oui",
        non: "Non",
        champAcomptePourcent: "Part demandée à la commande",
        aideAcomptePourcent: "En pourcentage du total.",
        acompteNote: "Le document écrira le mot « acompte » en toutes lettres. C'est ce mot qui rend la commande ferme des deux côtés : sans lui, la somme versée par un particulier est réputée être des arrhes, et il peut renoncer en l'abandonnant.",

        conditionsTitre: "Les dernières mentions",
        conditionsAide: "La date, la durée de validité et les modalités de paiement font partie de ce qu'un devis doit porter.",
        champNumero: "Numéro du devis",
        aideNumero: "Rien ne vous oblige à en mettre un, mais employez une série distincte de vos factures : leur numérotation, elle, doit rester continue.",
        champDateDevis: "Date du devis",
        champValidite: "Valable jusqu'au",
        aideValidite: "La durée est libre. Trente jours est l'usage, et cette date vous protège d'un client qui reviendrait six mois plus tard avec votre ancien prix.",
        champModalites: "Modalités de paiement",
        aideModalites: "Par exemple : virement bancaire sous 15 jours à réception de facture.",
        champDevisPayant: "Le devis est-il payant ?",
        champPrixDuDevis: "Prix du devis",
        aidePrixDuDevis: "Il doit être annoncé et chiffré sur le document lui-même.",

        signatureTitre: "Où et quand signez-vous ?",
        signatureAide: "Votre paraphe vient sous votre nom. Celui du client reste vide : c'est lui qui portera la mention « Bon pour accord ».",
        champLieuSignature: "Lieu de signature",
        signatureDessin: "Votre signature",
        signatureDessinAide: "Facultative. Tracez-la à la souris ou au doigt, ou laissez l'emplacement vide pour signer à la main après impression. Elle sera reprise par vos autres documents de la visite.",
        signatureReprise: "Reprise de votre document précédent. Retracez-la par-dessus ou effacez-la si ce n'est pas la bonne.",
        effacer: "Effacer",

        continuer: "Continuer",
        voirDocument: "Voir mon devis",
        resultatTitre: "Votre devis",
        montantTitre: "Total à payer",
        recapTitre: "Le détail",
        apercu: "Aperçu du devis",
        telecharger: "Télécharger en PDF",
        copier: "Copier le texte",
        copie: "Texte copié",
        recommencer: "Recommencer",

        manquesTitre: "Il manque encore quelque chose",
        manquesAide: "Complétez les champs signalés pour obtenir votre devis.",
        verifierTitre: "Une valeur à vérifier",
        verifierAide: "Ces champs sont remplis, mais il y a un problème dans ce qu'ils contiennent. Corrigez-les, puis revenez ici.",
        erreurFormat: "Ce format n'est pas reconnu.",
        erreurSiret: "Quatorze chiffres attendus.",
        erreurSiren: "Neuf chiffres attendus.",
        erreurMontant: "Indiquez un montant en euros, par exemple 145,50.",
        erreurQuantite: "Indiquez une quantité, par exemple 22,5.",
        erreurValiditeAvant: "La validité ne peut pas expirer avant la date du devis.",
        erreurPourcent: "Indiquez une part entre 1 et 100.",

        alerteSirenAbsent: "Vous n'avez pas indiqué le SIREN de votre client. Le devis reste valable, mais la facture qui suivra devra le porter à partir du 1er septembre 2027 : le plus simple est de le lui demander maintenant.",
        alerteValiditeDepassee: "La date de validité est déjà passée. Ce devis ne vaudrait plus rien le jour où vous le remettez.",
        alerteMentionBascule: "Votre devis reste valable après le 31 décembre 2026. Il porte la mention de sa date, mais la facture émise à partir du 1er janvier 2027 portera « {mention} » : les règles de TVA changent de code ce jour-là.",

        obligationTitre: "Ce que ce devis vous impose",
        obligationImpose: "Votre activité figure dans la liste où le devis écrit est obligatoire. Ne pas le remettre expose à une amende administrative, et vous prive de toute preuve du prix convenu le jour où le client conteste.",
        obligationPreuve: "Aucune règle n'impose de devis pour votre activité. Le code de la consommation vous impose en revanche d'informer votre client sur les caractéristiques, le prix et le délai avant qu'il ne s'engage, et ce document est la façon la plus simple de prouver que vous l'avez fait.",
        obligationUsage: "Entre professionnels, aucune règle générale n'impose de devis : c'est l'usage qui le veut. Il fixe le prix, la durée et le périmètre, et c'est la première pièce d'un dossier le jour où la facture reste impayée.",

        suiteTitre: "Et maintenant",
        suite1: "Remettez le devis avant de commencer : signé après coup, il ne prouve plus rien sur le prix convenu.",
        suite2: "Faites porter « Bon pour accord », la date et la signature par le client, puis gardez l'exemplaire signé : c'est lui qui fait le contrat.",
        suite3: "Un devis signé ne fait entrer aucun euro. Votre chiffre d'affaires se compte à l'encaissement, jamais à la signature ni à l'émission de la facture.",
        suite4: "Tout supplément décidé en cours de route se règle par un avenant écrit, daté et signé avant les travaux : sans lui, votre client n'est tenu que du montant du devis.",

        fondTitre: "Ce que la facturation électronique change ici",
        fond: "Rien sur le devis lui-même : vous l'envoyez toujours en PDF ou sur papier. La réforme vise les factures, qui devront passer par une plateforme habilitée à partir du 1er septembre 2027 pour une micro-entreprise. La facture qui suivra ce devis devra en revanche porter le SIREN de votre client professionnel.",
    },
    en: {
        phases: ["Your client", "The quotation", "The terms"],
        retour: "Back",
        prive: "Everything happens on your device: the quotation is built there, nobody receives your answers, not even us, and it all disappears with the tab.",

        clientTitre: "Who is this quotation for?",
        clientAide: "This answer decides three things: whether a quotation is compulsory, whether you ask for a company number, and what a payment on order will be worth.",
        clientPro: "A business",
        clientProDetail: "Company, association, public body",
        clientParticulier: "An individual",
        clientParticulierDetail: "A consumer, for private purposes",

        activiteTitre: "Is your activity on this list?",
        activiteAide: "A written quotation is compulsory for these, and failing to provide one exposes you to an administrative fine.",
        batiment: "Building and repairs",
        batimentDetail: "Plumbing, locksmithing, electrical work, painting, roofing",
        demenagement: "Removals",
        demenagementDetail: "Transport and handling of furniture",
        locationVoiture: "Car hire",
        locationVoitureDetail: "Passenger cars and light commercial vehicles",
        servicesALaPersonne: "Personal services at home",
        servicesALaPersonneDetail: "Cleaning, childcare, home help",
        optique: "Medical optics",
        optiqueDetail: "Prescription glasses and lenses",
        audioprothese: "Hearing aids",
        audioprotheseDetail: "Hearing devices and accessories",
        chirurgieEsthetique: "Cosmetic surgery",
        chirurgieEsthetiqueDetail: "Procedures for aesthetic purposes only",
        funeraire: "Funeral services",
        funeraireDetail: "Funerals, transport and burial",
        aucune: "None of these activities",
        aucuneDetail: "The quotation is then free, and strongly advised",
        listeComplete: "The detailed list, activity by activity, is kept up to date by Service Public Entreprendre: it goes down to amount thresholds this page does not repeat.",

        partiesTitre: "Who issues the quotation, and for whom",
        partiesAide: "These details go at the top of the quotation. Write the addresses as on an envelope, one line at a time.",
        champPrenoms: "Your forenames",
        champNom: "Your surname",
        champAdresse: "Your address",
        aideAdresse: "Street, then postcode and town, on separate lines.",
        champSiret: "Your SIRET",
        aideSiret: "Fourteen digits, no spaces.",
        champNomClient: "Client's name",
        aideNomClient: "The exact company name, or the person's name.",
        champAdresseClient: "Their address",
        champSirenClient: "Their SIREN",
        aideSirenClient: "Nine digits. Optional today, but your invoice will have to carry it from 1 September 2027: you may as well ask for it now.",
        champLieuExecution: "Place of performance",
        aideLieuExecution: "The site address, or wherever the work will be carried out.",

        prestationsTitre: "What you are pricing",
        prestationsAide: "An itemised breakdown is one of the compulsory particulars: every service and every supply, with quantity and unit price.",
        champObjet: "Subject of the quotation",
        aideObjet: "In a few words: « fitting a made-to-measure bookcase ».",
        colonneDesignation: "Description",
        colonneQuantite: "Quantity",
        colonnePrix: "Unit price",
        placeholderDesignation: "Fitting labour",
        ajouterLigne: "Add a line",
        retirerLigne: "Remove this line",
        choixManquant: "Choose an answer.",
        totalProvisoire: "Total",
        aideLignes: "Labour hours and travel costs go in like supplies, each on its own line.",

        prixTitre: "VAT and the deposit",
        prixAide: "Each of these two answers hides a trap: VAT written by mistake becomes payable, and a payment on order is not worth the same thing depending on the word used.",
        champTva: "Your position regarding VAT",
        franchise: "Exempt",
        redevable: "Liable",
        franchiseNote: "Your quotation will carry no tax line, only the wording « {mention} ». The price written is exactly the price paid.",
        franchiseDatee: "The wording follows the quotation's date: « TVA non applicable, article 293 B du CGI » until 31 December 2026, « TVA non applicable, article L. 233-3 du CIBS » from 1 January 2027, the day VAT rules move to a new code.",
        redevableNote: "Any VAT mentioned becomes payable purely by appearing on the document. Check your rate before writing it.",
        champTauxTva: "Your VAT rate",
        aideTauxTva: "As a percentage.",
        champAcompte: "Are you asking for a deposit on order?",
        oui: "Yes",
        non: "No",
        champAcomptePourcent: "Share due on order",
        aideAcomptePourcent: "As a percentage of the total.",
        acompteNote: "The document will spell out the word « acompte ». That word is what makes the order firm on both sides: without it, money paid by a private client counts as « arrhes », and they can walk away by simply forfeiting it.",

        conditionsTitre: "The last particulars",
        conditionsAide: "The date, the period of validity and the payment terms are part of what a quotation must carry.",
        champNumero: "Quotation number",
        aideNumero: "Nothing obliges you to use one, but use a series separate from your invoices: their numbering has to stay unbroken.",
        champDateDevis: "Date of the quotation",
        champValidite: "Valid until",
        aideValidite: "The period is up to you. Thirty days is customary, and this date protects you from a client coming back six months later with your old price.",
        champModalites: "Payment terms",
        aideModalites: "For example: bank transfer within 15 days of invoice.",
        champDevisPayant: "Is the quotation chargeable?",
        champPrixDuDevis: "Price of the quotation",
        aidePrixDuDevis: "It has to be announced and priced on the document itself.",

        signatureTitre: "Where and when are you signing?",
        signatureAide: "Your signature goes under your name. The client's box stays empty: that is where they write « Bon pour accord ».",
        champLieuSignature: "Place of signature",
        signatureDessin: "Your signature",
        signatureDessinAide: "Optional. Draw it with the mouse or your finger, or leave the space empty and sign by hand after printing. Your other documents in this visit will reuse it.",
        signatureReprise: "Taken from your previous document. Draw over it, or clear it if it is not the right one.",
        effacer: "Clear",

        continuer: "Continue",
        voirDocument: "See my quotation",
        resultatTitre: "Your quotation",
        montantTitre: "Total payable",
        recapTitre: "The detail",
        apercu: "Quotation preview",
        telecharger: "Download as PDF",
        copier: "Copy the text",
        copie: "Text copied",
        recommencer: "Start again",

        manquesTitre: "Something is still missing",
        manquesAide: "Fill in the highlighted fields to get your quotation.",
        verifierTitre: "Something to check",
        verifierAide: "These fields are filled in, but there is a problem with what they contain. Correct them, then come back here.",
        erreurFormat: "This format is not recognised.",
        erreurSiret: "Fourteen digits expected.",
        erreurSiren: "Nine digits expected.",
        erreurMontant: "Enter an amount in euros, for example 145.50.",
        erreurQuantite: "Enter a quantity, for example 22.5.",
        erreurValiditeAvant: "Validity cannot expire before the date of the quotation.",
        erreurPourcent: "Enter a share between 1 and 100.",

        alerteSirenAbsent: "You have not given your client's SIREN. The quotation is still valid, but the invoice that follows will have to carry it from 1 September 2027: the easiest time to ask for it is now.",
        alerteValiditeDepassee: "The validity date has already passed. This quotation would be worthless the day you hand it over.",
        alerteMentionBascule: "Your quotation stays valid past 31 December 2026. It carries the wording of its own date, but an invoice issued from 1 January 2027 will carry « {mention} »: VAT rules move to a new code that day.",

        obligationTitre: "What this quotation requires of you",
        obligationImpose: "Your activity is on the list where a written quotation is compulsory. Failing to provide one exposes you to an administrative fine, and leaves you with no proof of the agreed price the day your client disputes it.",
        obligationPreuve: "No rule imposes a quotation for your activity. The consumer code does require you to inform your client about the features, the price and the timescale before they commit, and this document is the simplest way to prove you did.",
        obligationUsage: "Between businesses, no general rule imposes a quotation: custom does. It sets the price, the timescale and the scope, and it is the first document in your file the day the invoice goes unpaid.",

        suiteTitre: "What happens next",
        suite1: "Hand over the quotation before you start: signed afterwards, it no longer proves anything about the agreed price.",
        suite2: "Have the client write « Bon pour accord », the date and their signature, then keep the signed copy: that is what makes the contract.",
        suite3: "A signed quotation brings in no money. Your turnover counts on collection, never on signature nor on issuing the invoice.",
        suite4: "Any extra decided along the way goes through a written amendment, dated and signed before the work: without one, your client only owes the amount on the quotation.",

        fondTitre: "What electronic invoicing changes here",
        fond: "Nothing on the quotation itself: you still send it as a PDF or on paper. The reform covers invoices, which will have to go through an approved platform from 1 September 2027 for a micro-enterprise. The invoice that follows this quotation will, however, have to carry your business client's SIREN.",
    },
};

const LIBELLES_MANQUES = {
    fr: {
        client: "La qualité de votre client",
        prenoms: "Vos prénoms",
        nom: "Votre nom",
        adresse: "Votre adresse",
        siret: "Votre SIRET",
        numero: "Le numéro du devis",
        objet: "L'objet du devis",
        nomClient: "Le nom du client",
        adresseClient: "L'adresse du client",
        sirenClient: "Le SIREN du client",
        lieuExecution: "Le lieu d'exécution",
        lignes: "Au moins une prestation chiffrée",
        dateDevis: "La date du devis",
        validiteJusquAu: "La date de fin de validité",
        tauxTva: "Votre taux de TVA",
        acomptePourcent: "La part demandée à la commande",
        modalitesDePaiement: "Les modalités de paiement",
        prixDuDevis: "Le prix du devis",
        lieuDeSignature: "Le lieu de signature",
    },
    en: {
        client: "Whether your client is a business",
        prenoms: "Your forenames",
        nom: "Your surname",
        adresse: "Your address",
        siret: "Your SIRET",
        numero: "The quotation number",
        objet: "The subject of the quotation",
        nomClient: "The client's name",
        adresseClient: "The client's address",
        sirenClient: "The client's SIREN",
        lieuExecution: "The place of performance",
        lignes: "At least one priced item",
        dateDevis: "The date of the quotation",
        validiteJusquAu: "The end of validity date",
        tauxTva: "Your VAT rate",
        acomptePourcent: "The share due on order",
        modalitesDePaiement: "The payment terms",
        prixDuDevis: "The price of the quotation",
        lieuDeSignature: "The place of signature",
    },
};

const PHASES_PAR_ECRAN = {
    client: 0,
    activite: 0,
    parties: 1,
    prestations: 1,
    prix: 1,
    conditions: 2,
    signature: 2,
    resultat: 2,
};

const LIGNE_VIDE = { designation: "", quantite: "", prixUnitaire: "" };

const VIDE = {
    client: "",
    activiteReglementee: "",
    prenoms: "",
    nom: "",
    adresse: "",
    siret: "",
    nomClient: "",
    adresseClient: "",
    sirenClient: "",
    lieuExecution: "",
    objet: "",
    lignes: [],
    tva: "",
    tauxTva: "",
    acompte: "",
    acomptePourcent: "",
    numero: "",
    dateDevis: "",
    validiteJusquAu: "",
    modalitesDePaiement: "",
    devisPayant: "",
    prixDuDevis: "",
    lieuDeSignature: "",
    dateDeSignature: "",
};

/**
 * L'icône et la précision de chaque famille d'activité.
 *
 * La table est INDEXÉE SUR LES CLÉS DE LA LIB, et le parcours la parcourt dans
 * cet ordre : ajouter une neuvième famille à `ACTIVITES_DEVIS_OBLIGATOIRE` sans
 * lui donner d'icône ici la ferait s'afficher sans rien, puisque SolarIcon rend
 * du vide en silence sur un nom inconnu. Le harnais compare les deux listes.
 *
 * Chaque famille porte SON icône : sept cases à cocher identiques ne disent
 * rien, n'aident pas à repérer la sienne, et font une grille grise.
 */
const FAMILLES = [
    { cle: "batiment", icone: "paint-roller-linear" },
    { cle: "demenagement", icone: "box-minimalistic-linear" },
    { cle: "locationVoiture", icone: "wheel-linear" },
    { cle: "servicesALaPersonne", icone: "hand-heart-linear" },
    { cle: "optique", icone: "glasses-linear" },
    { cle: "audioprothese", icone: "volume-loud-linear" },
    { cle: "chirurgieEsthetique", icone: "stethoscope-linear" },
    { cle: "funeraire", icone: "leaf-linear" },
];

/**
 * Une entrée de liste : l'icône à GAUCHE, le libellé, sa précision dessous.
 *
 * `Choix` empile l'icône au-dessus du titre, ce qui va très bien à deux ou
 * trois grandes cartes de situation. À huit familles d'activité, la même carte
 * devient une colonne de pavés hauts qu'on parcourt à la verticale : ici, c'est
 * une liste qui doit se balayer.
 */
function Categorie({ icone, titre, detail, onClick }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="group h-full w-full text-left flex items-start gap-3.5 px-4 py-3.5 rounded-xl border border-gray-200/70 dark:border-white/10 hover:border-primary/60 hover:bg-primary/[0.04] transition-colors duration-200"
        >
            <SolarIcon
                name={icone}
                className="mt-0.5 text-xl shrink-0 text-gray-400 dark:text-lightBlue/60 group-hover:text-primary transition-colors duration-200"
            />
            <span className="min-w-0">
                <span className="block font-semibold dark:text-white leading-snug">{titre}</span>
                {detail && (
                    <span className="block text-xs text-gray-500 dark:text-lightBlue/70 mt-0.5 leading-snug">
                        {detail}
                    </span>
                )}
            </span>
        </button>
    );
}

/** Une date ISO décalée de n jours, pour proposer une validité par défaut. */
function dansNJours(iso, n) {
    const d = new Date(`${iso}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + n);
    return d.toISOString().slice(0, 10);
}

/**
 * Les lignes du devis, ajoutables et retirables.
 *
 * Le seul endroit de la rubrique où le lecteur produit une liste plutôt que de
 * répondre à une question. Une ligne vide reste toujours en dernier : sans
 * elle, il faudrait deviner qu'un bouton l'ajoute.
 */
function Prestations({ t, lignes, poser, erreurs, manques = {} }) {
    const modifier = (rang, champ) => (valeur) =>
        poser(lignes.map((l, i) => (i === rang ? { ...l, [champ]: valeur } : l)));

    return (
        <div className="space-y-3">
            {lignes.map((ligne, rang) => (
                // Les lignes n'ont pas d'identité propre : leur rang EST leur
                // identité tant qu'on ne les réordonne pas, et rien ici ne les
                // réordonne.
                <div key={rang} className="rounded-xl border border-gray-200/70 dark:border-white/10 p-4">
                    <div className="flex items-start gap-3">
                        <div className="flex-1 space-y-3">
                            <label className="block">
                                <span className="block text-xs text-gray-500 dark:text-lightBlue/70 mb-1.5">
                                    {t.colonneDesignation}
                                </span>
                                <input
                                    id={`devis-ligne${rang}-designation`}
                                    type="text"
                                    autoComplete="off"
                                    aria-invalid={manques[`ligne${rang}-designation`] || undefined}
                                    value={ligne.designation}
                                    placeholder={t.placeholderDesignation}
                                    onChange={(e) => modifier(rang, "designation")(e.target.value)}
                                    className={`${STYLE_CHAMP} ${
                                        manques[`ligne${rang}-designation`]
                                            ? "border-red-500/60"
                                            : erreurs[`ligne${rang}.designation`]
                                            ? "border-amber-500/60"
                                            : "border-gray-200/70 dark:border-white/10"
                                    }`}
                                />
                            </label>
                            <div className="grid grid-cols-2 gap-3">
                                <Champ
                                    libelle={t.colonneQuantite}
                                    valeur={ligne.quantite}
                                    poser={modifier(rang, "quantite")}
                                    erreur={erreurs[`ligne${rang}.quantite`]}
                                    manquant={manques[`ligne${rang}-quantite`]}
                                    id={`devis-ligne${rang}-quantite`}
                                    inputMode="decimal"
                                />
                                <Champ
                                    libelle={t.colonnePrix}
                                    valeur={ligne.prixUnitaire}
                                    poser={modifier(rang, "prixUnitaire")}
                                    erreur={erreurs[`ligne${rang}.prixUnitaire`]}
                                    manquant={manques[`ligne${rang}-prixUnitaire`]}
                                    id={`devis-ligne${rang}-prixUnitaire`}
                                    inputMode="decimal"
                                />
                            </div>
                        </div>
                        {lignes.length > 1 && (
                            <button
                                type="button"
                                onClick={() => poser(lignes.filter((_, i) => i !== rang))}
                                aria-label={t.retirerLigne}
                                title={t.retirerLigne}
                                className="mt-3 -mx-3 shrink-0 inline-flex min-h-[44px] min-w-[44px] items-center justify-center text-gray-400 dark:text-lightBlue/60 hover:text-primary transition-colors duration-200"
                            >
                                <SolarIcon name="trash-bin-minimalistic-linear" className="text-lg" />
                            </button>
                        )}
                    </div>
                </div>
            ))}
            <button
                type="button"
                onClick={() => poser([...lignes, { ...LIGNE_VIDE }])}
                className="-my-2.5 inline-flex min-h-[44px] items-center gap-2 text-sm text-gray-600 dark:text-lightBlue hover:text-primary transition-colors duration-200"
            >
                <SolarIcon name="add-circle-linear" className="text-lg" />
                {t.ajouterLigne}
            </button>
        </div>
    );
}

export default function Devis({ lang = "fr", aujourdhui }) {
    const t = L[lang] || L.fr;
    const { identite } = useDossierOutil();
    const [r, setR] = useState(() => ({ ...VIDE, ...identite }));
    const [copie, setCopie] = useState(false);
    // Les champs signalés au dernier clic sur « Continuer » (voir essayer()).
    const [tentative, setTentative] = useState(null);
    const { signature, poser: poserSignature } = useSignature();

    const { etape, sens, reduit, rang, aller, retour, recommencer, communs } = useParcours(
        "client",
        () => {
            setR({ ...VIDE, ...identite });
            setCopie(false);
        },
        PHASES_PAR_ECRAN,
        null,
        // Reprendre où l'on en était, le temps de l'onglet (Journey.jsx).
        { r, setR },
    );

    useSignalerIdentite(
        { prenoms: r.prenoms, nom: r.nom, siret: r.siret },
        ["prestations", "prix", "conditions", "signature", "resultat"].includes(etape),
    );

    const poser = (cle) => (valeur) => {
        setR((p) => ({ ...p, [cle]: valeur }));
        // Le rouge d'un champ signalé s'efface dès qu'on y touche ; pour les
        // lignes de prestation, toutes leurs cases à la fois.
        setTentative((tp) => {
            if (!tp) return tp;
            const cles = Object.fromEntries(
                Object.entries(tp.cles).filter(([k]) => (cle === "lignes" ? !k.startsWith("ligne") : k !== cle)),
            );
            return { ...tp, cles };
        });
    };

    const jour = String(aujourdhui).slice(0, 10);
    /* Une ligne vide attend toujours en dernier, sinon rien ne dirait qu'on
       peut en ajouter. Elle passe par un mémo : recréée à chaque rendu, elle
       ferait recalculer tout le devis pour rien. */
    const lignes = useMemo(() => (r.lignes.length ? r.lignes : [{ ...LIGNE_VIDE }]), [r.lignes]);

    /* Les valeurs proposées d'office : la date du jour, une validité de trente
       jours qui est l'usage, et un numéro dans une série distincte des
       factures. Trois champs de saisie, qui se relisent et se corrigent. */
    const reponses = useMemo(
        () => ({
            ...r,
            lignes,
            numero: r.numero || `D-${jour.slice(0, 4)}-001`,
            dateDevis: r.dateDevis || jour,
            validiteJusquAu: r.validiteJusquAu || dansNJours(jour, 30),
            dateDeSignature: r.dateDeSignature || jour,
        }),
        [r, lignes, jour],
    );

    const controle = useMemo(() => valider(reponses, aujourdhui), [reponses, aujourdhui]);
    const devis = useMemo(() => composerDevis(reponses, aujourdhui), [reponses, aujourdhui]);
    const compte = useMemo(() => totaux(reponses), [reponses]);
    const recap = useMemo(() => recapitulatif(reponses, aujourdhui), [reponses, aujourdhui]);
    const regle = obligation(reponses);
    const versement = natureDuVersement(reponses);

    /* Les refus, rangés par champ : le parcours les montre à l'écran où la
       valeur se saisit, pas seulement au résultat. */
    const erreurs = useMemo(() => {
        const table = {};
        for (const { champ, motif } of controle.invalides || []) {
            if (champ === "siret") table[champ] = t.erreurSiret;
            else if (champ === "sirenClient") table[champ] = t.erreurSiren;
            else if (champ.endsWith(".quantite")) table[champ] = t.erreurQuantite;
            else if (champ.endsWith(".prixUnitaire")) table[champ] = t.erreurMontant;
            else if (champ === "prixDuDevis") table[champ] = t.erreurMontant;
            else if (champ === "acomptePourcent") table[champ] = t.erreurPourcent;
            else if (motif === "avantLeDevis") table[champ] = t.erreurValiditeAvant;
            else table[champ] = t.erreurFormat;
        }
        return table;
    }, [controle, t]);

    const rempli = (...cles) => cles.every((c) => String(r[c] || "").trim());
    const commun = { ...communs, phases: t.phases, retour: t.retour, rang };

    /* « Continuer » reste actif. Un bouton grisé ne dit pas ce qui manque :
       au clic, chaque champ vide s'entoure de rouge et le focus va au
       premier. Les refus de format gardent leur message propre. */
    const manques = tentative?.etape === etape ? tentative.cles : {};
    const essayer = (verifs, suivante) => {
        const manquants = verifs.filter(([, ok]) => !ok).map(([cle]) => cle);
        if (!manquants.length) {
            setTentative(null);
            aller(suivante);
            return;
        }
        setTentative({ etape, cles: Object.fromEntries(manquants.map((cle) => [cle, true])) });
        requestAnimationFrame(() => {
            const cible = document.getElementById(`devis-${manquants[0]}`);
            const champ = cible?.matches("input, textarea, button")
                ? cible
                : cible?.querySelector("input, textarea, button");
            champ?.focus();
        });
    };
    const champRempli = (cle) => [cle, rempli(cle)];
    /* Une ligne de prestation compte dès qu'une de ses trois cases est
       remplie ; la dernière, vide, attend. Sans aucune ligne, c'est la
       première désignation qui manque. */
    const verifsLignes = () => {
        const verifs = [];
        lignes.forEach((l, i) => {
            const vide = !String(l.designation || "").trim() && !String(l.quantite || "").trim() && !String(l.prixUnitaire || "").trim();
            if (vide) return;
            verifs.push([`ligne${i}-designation`, Boolean(String(l.designation || "").trim())]);
            verifs.push([`ligne${i}-quantite`, Boolean(String(l.quantite || "").trim()) && !erreurs[`ligne${i}.quantite`]]);
            verifs.push([`ligne${i}-prixUnitaire`, Boolean(String(l.prixUnitaire || "").trim()) && !erreurs[`ligne${i}.prixUnitaire`]]);
        });
        if (!verifs.length) verifs.push(["ligne0-designation", false]);
        /* Filet : la règle qui fait foi reste celle du total, qui ne se
           calcule que sur des lignes complètes et valides. */
        if (verifs.every(([, ok]) => ok) && !compte) verifs.push(["ligne0-designation", false]);
        return verifs;
    };
    /* Un choix en boutons n'a pas de bord à rougir : une phrase le signale. */
    const choixManquant = (cle) =>
        manques[cle] ? (
            <p className="text-xs text-red-700 dark:text-red-400 mt-1.5 mb-0">{t.choixManquant}</p>
        ) : null;

    const telecharger = () =>
        telechargerDocument({
            document: devis,
            signature,
            nomFichier: nomDuFichier(`devis-${reponses.numero}`, r),
        });

    const copier = async () => {
        if (!devis) return;
        try {
            await navigator.clipboard.writeText(documentEnTexte(devis));
            setCopie(true);
            window.setTimeout(() => setCopie(false), 2000);
        } catch {
            // Presse-papiers refusé : le PDF reste la voie normale.
        }
    };

    /* ---------------------------------------------------------------- */
    /* Le tri                                                            */
    /* ---------------------------------------------------------------- */

    if (etape === "client") {
        return (
            <Ecran key={etape} {...commun} phase={0} titre={t.clientTitre} aide={t.clientAide}>
                <div className="grid sm:grid-cols-2 gap-3">
                    <Choix
                        icone="buildings-2-linear"
                        titre={t.clientPro}
                        detail={t.clientProDetail}
                        onClick={() => {
                            setR((p) => ({ ...p, client: "professionnel" }));
                            aller("activite");
                        }}
                    />
                    <Choix
                        icone="user-rounded-linear"
                        titre={t.clientParticulier}
                        detail={t.clientParticulierDetail}
                        onClick={() => {
                            setR((p) => ({ ...p, client: "particulier" }));
                            aller("activite");
                        }}
                    />
                </div>
                <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-5 mb-0">{t.prive}</p>
            </Ecran>
        );
    }

    if (etape === "activite") {
        const choisir = (cle) => () => {
            setR((p) => ({ ...p, activiteReglementee: cle }));
            aller("parties");
        };
        return (
            <Ecran key={etape} {...commun} phase={0} onRetour={retour} titre={t.activiteTitre} aide={t.activiteAide}>
                {/* Huit familles, donc quatre rangées pleines : aucune carte
                    ne reste seule au bout d'une ligne. « Aucune » se détache
                    en dessous, derrière un filet : c'est l'échappatoire, pas
                    une neuvième famille. */}
                <div className="grid sm:grid-cols-2 gap-3 items-stretch">
                    {FAMILLES.map(({ cle, icone }) => (
                        <Categorie
                            key={cle}
                            icone={icone}
                            titre={t[cle]}
                            detail={t[`${cle}Detail`]}
                            onClick={choisir(cle)}
                        />
                    ))}
                </div>
                <div className="mt-3 pt-3 border-t border-gray-200/70 dark:border-white/10">
                    <Categorie
                        icone="close-circle-linear"
                        titre={t.aucune}
                        detail={t.aucuneDetail}
                        onClick={choisir("aucune")}
                    />
                </div>
                <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-5 mb-0">{t.listeComplete}</p>
            </Ecran>
        );
    }

    if (etape === "parties") {
        const professionnel = r.client === "professionnel";
        return (
            <Ecran key={etape} {...commun} phase={1} onRetour={retour} titre={t.partiesTitre} aide={t.partiesAide}>
                <div className="space-y-5">
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Champ libelle={t.champPrenoms} valeur={r.prenoms} poser={poser("prenoms")} id="devis-prenoms" manquant={manques.prenoms} />
                        <Champ libelle={t.champNom} valeur={r.nom} poser={poser("nom")} id="devis-nom" manquant={manques.nom} />
                    </div>
                    <Champ
                        type="textarea"
                        libelle={t.champAdresse}
                        aide={t.aideAdresse}
                        valeur={r.adresse}
                        poser={poser("adresse")}
                        id="devis-adresse"
                        manquant={manques.adresse}
                        rows={2}
                    />
                    <Champ
                        libelle={t.champSiret}
                        aide={t.aideSiret}
                        valeur={r.siret}
                        poser={poser("siret")}
                        erreur={erreurs.siret}
                        id="devis-siret"
                        manquant={manques.siret}
                        inputMode="numeric"
                    />
                    <Champ
                        libelle={t.champNomClient}
                        aide={t.aideNomClient}
                        valeur={r.nomClient}
                        poser={poser("nomClient")}
                        id="devis-nomClient"
                        manquant={manques.nomClient}
                    />
                    <Champ
                        type="textarea"
                        libelle={t.champAdresseClient}
                        valeur={r.adresseClient}
                        poser={poser("adresseClient")}
                        id="devis-adresseClient"
                        manquant={manques.adresseClient}
                        rows={2}
                    />
                    {professionnel && (
                        <Champ
                            libelle={t.champSirenClient}
                            aide={t.aideSirenClient}
                            valeur={r.sirenClient}
                            poser={poser("sirenClient")}
                            erreur={erreurs.sirenClient}
                            id="devis-sirenClient"
                            manquant={manques.sirenClient}
                            inputMode="numeric"
                        />
                    )}
                    <Champ
                        libelle={t.champLieuExecution}
                        aide={t.aideLieuExecution}
                        valeur={r.lieuExecution}
                        poser={poser("lieuExecution")}
                        id="devis-lieuExecution"
                        manquant={manques.lieuExecution}
                    />
                </div>
                <Avancer
                    pleineLargeur
                    onClick={() =>
                        essayer(
                            [
                                champRempli("prenoms"),
                                champRempli("nom"),
                                champRempli("adresse"),
                                ["siret", rempli("siret") && !erreurs.siret],
                                champRempli("nomClient"),
                                champRempli("adresseClient"),
                                ...(professionnel ? [["sirenClient", !erreurs.sirenClient]] : []),
                                champRempli("lieuExecution"),
                            ],
                            "prestations",
                        )
                    }
                >
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "prestations") {
        const chiffrees = compte;
        return (
            <Ecran
                key={etape}
                {...commun}
                phase={1}
                onRetour={retour}
                titre={t.prestationsTitre}
                aide={t.prestationsAide}
            >
                <div className="space-y-5">
                    <Champ libelle={t.champObjet} aide={t.aideObjet} valeur={r.objet} poser={poser("objet")} id="devis-objet" manquant={manques.objet} />
                    <div>
                        <Prestations t={t} lignes={lignes} poser={poser("lignes")} erreurs={erreurs} manques={manques} />
                        <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-4 mb-0">{t.aideLignes}</p>
                    </div>
                    {chiffrees && (
                        <div className="flex items-baseline justify-between gap-3 rounded-xl border border-gray-200/70 dark:border-white/10 px-5 py-4">
                            <span className="text-sm text-gray-500 dark:text-lightBlue/70">{t.totalProvisoire}</span>
                            <span className="text-xl font-bold dark:text-white">{euros(chiffrees.total)}</span>
                        </div>
                    )}
                </div>
                <Avancer
                    pleineLargeur
                    onClick={() => essayer([champRempli("objet"), ...verifsLignes()], "prix")}
                >
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "prix") {
        return (
            <Ecran key={etape} {...commun} phase={1} onRetour={retour} titre={t.prixTitre} aide={t.prixAide}>
                <div className="space-y-6">
                    <div id="devis-tva">
                        <Segments
                            libelle={t.champTva}
                            options={[
                                { cle: "franchise", libelle: t.franchise },
                                { cle: "redevable", libelle: t.redevable },
                            ]}
                            valeur={r.tva}
                            poser={poser("tva")}
                        />
                        {choixManquant("tva")}
                        {r.tva === "franchise" && (
                            <div className="mt-3 space-y-2">
                                <p className="text-sm leading-7 text-gray-600 dark:text-lightBlue mb-0">
                                    {remplir(t.franchiseNote, { mention: mentionFranchise(reponses.dateDevis) })}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-lightBlue/70 mb-0">{t.franchiseDatee}</p>
                            </div>
                        )}
                        {r.tva === "redevable" && (
                            <div className="mt-3 space-y-4">
                                <p className="text-sm leading-7 text-amber-700 dark:text-amber-400 mb-0">
                                    {t.redevableNote}
                                </p>
                                <Champ
                                    libelle={t.champTauxTva}
                                    aide={t.aideTauxTva}
                                    valeur={r.tauxTva}
                                    poser={poser("tauxTva")}
                                    erreur={erreurs.tauxTva}
                                    id="devis-tauxTva"
                                    manquant={manques.tauxTva}
                                    inputMode="decimal"
                                />
                            </div>
                        )}
                    </div>

                    <div id="devis-acompte">
                        <Segments
                            libelle={t.champAcompte}
                            options={[
                                { cle: "oui", libelle: t.oui },
                                { cle: "non", libelle: t.non },
                            ]}
                            valeur={r.acompte}
                            poser={poser("acompte")}
                        />
                        {choixManquant("acompte")}
                        {r.acompte === "oui" && (
                            <div className="mt-3 space-y-4">
                                <Champ
                                    libelle={t.champAcomptePourcent}
                                    aide={t.aideAcomptePourcent}
                                    valeur={r.acomptePourcent}
                                    poser={poser("acomptePourcent")}
                                    erreur={erreurs.acomptePourcent}
                                    id="devis-acomptePourcent"
                                    manquant={manques.acomptePourcent}
                                    inputMode="decimal"
                                />
                                {versement.risqueSiMuet && (
                                    <p className="text-sm leading-7 text-gray-600 dark:text-lightBlue mb-0">
                                        {t.acompteNote}
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                <Avancer
                    pleineLargeur
                    onClick={() =>
                        essayer(
                            [
                                ["tva", Boolean(r.tva)],
                                ...(r.tva === "redevable" ? [["tauxTva", rempli("tauxTva") && !erreurs.tauxTva]] : []),
                                ["acompte", Boolean(r.acompte)],
                                ...(r.acompte === "oui"
                                    ? [["acomptePourcent", rempli("acomptePourcent") && !erreurs.acomptePourcent]]
                                    : []),
                            ],
                            "conditions",
                        )
                    }
                >
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "conditions") {
        return (
            <Ecran
                key={etape}
                {...commun}
                phase={2}
                onRetour={retour}
                titre={t.conditionsTitre}
                aide={t.conditionsAide}
            >
                <div className="space-y-5">
                    <Champ
                        libelle={t.champNumero}
                        aide={t.aideNumero}
                        valeur={r.numero || reponses.numero}
                        poser={poser("numero")}
                    />
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Champ
                            type="date"
                            libelle={t.champDateDevis}
                            valeur={r.dateDevis || jour}
                            poser={poser("dateDevis")}
                            erreur={erreurs.dateDevis}
                        />
                        <Champ
                            type="date"
                            libelle={t.champValidite}
                            aide={t.aideValidite}
                            valeur={r.validiteJusquAu || reponses.validiteJusquAu}
                            poser={poser("validiteJusquAu")}
                            erreur={erreurs.validiteJusquAu}
                            id="devis-validiteJusquAu"
                            manquant={manques.validiteJusquAu}
                        />
                    </div>
                    <Champ
                        libelle={t.champModalites}
                        aide={t.aideModalites}
                        valeur={r.modalitesDePaiement}
                        poser={poser("modalitesDePaiement")}
                        id="devis-modalitesDePaiement"
                        manquant={manques.modalitesDePaiement}
                    />
                    <div id="devis-devisPayant">
                        <Segments
                            libelle={t.champDevisPayant}
                            options={[
                                { cle: "non", libelle: t.non },
                                { cle: "oui", libelle: t.oui },
                            ]}
                            valeur={r.devisPayant}
                            poser={poser("devisPayant")}
                        />
                        {choixManquant("devisPayant")}
                        {r.devisPayant === "oui" && (
                            <div className="mt-3">
                                <Champ
                                    libelle={t.champPrixDuDevis}
                                    aide={t.aidePrixDuDevis}
                                    valeur={r.prixDuDevis}
                                    poser={poser("prixDuDevis")}
                                    erreur={erreurs.prixDuDevis}
                                    id="devis-prixDuDevis"
                                    manquant={manques.prixDuDevis}
                                    inputMode="decimal"
                                />
                            </div>
                        )}
                    </div>
                </div>
                <Avancer
                    pleineLargeur
                    onClick={() =>
                        essayer(
                            [
                                ["validiteJusquAu", !erreurs.validiteJusquAu],
                                champRempli("modalitesDePaiement"),
                                ["devisPayant", Boolean(r.devisPayant)],
                                ...(r.devisPayant === "oui"
                                    ? [["prixDuDevis", rempli("prixDuDevis") && !erreurs.prixDuDevis]]
                                    : []),
                            ],
                            "signature",
                        )
                    }
                >
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "signature") {
        return (
            <Ecran key={etape} {...commun} phase={2} onRetour={retour} titre={t.signatureTitre} aide={t.signatureAide}>
                <div className="space-y-5">
                    <Champ
                        libelle={t.champLieuSignature}
                        valeur={r.lieuDeSignature}
                        poser={poser("lieuDeSignature")}
                        id="devis-lieuDeSignature"
                        manquant={manques.lieuDeSignature}
                    />
                    <PadSignature
                        libelle={t.signatureDessin}
                        aide={t.signatureDessinAide}
                        effacer={t.effacer}
                        repriseLibelle={t.signatureReprise}
                        valeur={signature}
                        poser={poserSignature}
                    />
                </div>
                <Avancer pleineLargeur onClick={() => essayer([champRempli("lieuDeSignature")], "resultat")}>
                    {t.voirDocument}
                </Avancer>
            </Ecran>
        );
    }

    /* ---------------------------------------------------------------- */
    /* Le résultat                                                       */
    /* ---------------------------------------------------------------- */

    return (
        <Resultat onRetour={retour} retour={t.retour} key={etape} titre={t.resultatTitre} phases={t.phases} sens={sens} reduit={reduit}>
            {!devis ? (
                <div className="mt-4">
                    <div className="font-bold text-lg dark:text-white leading-snug">
                        {controle.manques.length ? t.manquesTitre : t.verifierTitre}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">
                        {controle.manques.length ? t.manquesAide : t.verifierAide}
                    </p>
                    <ul className="mt-4 space-y-2">
                        {controle.manques.map((cle) => (
                            <li key={cle} className="flex gap-3 text-[15px] leading-7 text-gray-600 dark:text-lightBlue">
                                <span className="mt-[11px] h-1.5 w-1.5 shrink-0 rounded-full bg-red-500/70" />
                                <span>{LIBELLES_MANQUES[lang]?.[cle] || LIBELLES_MANQUES.fr[cle]}</span>
                            </li>
                        ))}
                        {(controle.invalides || []).map(({ champ, motif, rang: ligne }) => (
                            <li
                                key={`${champ}-${motif}`}
                                className="flex gap-3 text-[15px] leading-7 text-gray-600 dark:text-lightBlue"
                            >
                                <span className="mt-[11px] h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500/70" />
                                <span>
                                    {champ.startsWith("ligne")
                                        ? `${t.colonneDesignation} ${ligne + 1}`
                                        : LIBELLES_MANQUES[lang]?.[champ] || LIBELLES_MANQUES.fr[champ]}
                                    {" : "}
                                    {erreurs[champ]}
                                </span>
                            </li>
                        ))}
                    </ul>
                    <Avancer pleineLargeur onClick={retour}>
                        {t.retour}
                    </Avancer>
                </div>
            ) : (
                <div className="mt-4">
                    <div className="rounded-2xl border border-gray-200/70 dark:border-white/10 p-5 mb-6">
                        <div className="text-[11px] font-semibold text-gray-500 dark:text-lightBlue/70 uppercase tracking-wider mb-2">
                            {t.montantTitre}
                        </div>
                        <div className="text-3xl font-bold dark:text-white">{euros(compte.total)}</div>
                    </div>

                    {controle.alertes.includes("validiteDepassee") && (
                        <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-4 mb-5">
                            <p className="text-sm text-gray-700 dark:text-lightBlue mb-0">
                                {t.alerteValiditeDepassee}
                            </p>
                        </div>
                    )}
                    {controle.alertes.includes("mentionBascule") && (
                        <div className="rounded-xl border border-gray-200/70 dark:border-white/10 p-4 mb-5">
                            <p className="text-sm text-gray-700 dark:text-lightBlue mb-0">
                                {remplir(t.alerteMentionBascule, { mention: MENTION_FRANCHISE_CIBS })}
                            </p>
                        </div>
                    )}
                    {controle.alertes.includes("sirenClientAbsent") && (
                        <div className="rounded-xl border border-gray-200/70 dark:border-white/10 p-4 mb-5">
                            <p className="text-sm text-gray-700 dark:text-lightBlue mb-0">{t.alerteSirenAbsent}</p>
                        </div>
                    )}

                    <div className="text-[11px] font-semibold text-gray-500 dark:text-lightBlue/70 uppercase tracking-wider mb-3">
                        {t.recapTitre}
                    </div>
                    <div className="rounded-2xl border border-gray-200/70 dark:border-white/10 overflow-hidden mb-6">
                        <table className="w-full text-sm">
                            <tbody>
                                {recap.map(([titre, valeur]) => (
                                    <tr
                                        key={titre}
                                        className="border-b last:border-b-0 border-gray-200/70 dark:border-white/10"
                                    >
                                        <th
                                            scope="row"
                                            className="px-5 py-3 text-left font-normal text-gray-500 dark:text-lightBlue/70 align-top w-2/5"
                                        >
                                            {titre}
                                        </th>
                                        <td className="px-5 py-3 font-medium dark:text-white">{valeur}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="mb-6 rounded-2xl border border-gray-200/70 dark:border-white/10 bg-white/60 dark:bg-white/[0.03] p-5">
                        <div className="flex items-center gap-2.5 mb-3">
                            <SolarIcon
                                name="shield-check-linear"
                                className="text-xl shrink-0 text-gray-400 dark:text-lightBlue/60"
                            />
                            <div className="font-semibold dark:text-white leading-snug">{t.obligationTitre}</div>
                        </div>
                        <p className="text-sm leading-7 text-gray-600 dark:text-lightBlue mb-0">
                            {regle.niveau === "impose"
                                ? t.obligationImpose
                                : regle.niveau === "preuve"
                                  ? t.obligationPreuve
                                  : t.obligationUsage}
                        </p>
                    </div>

                    <div className="text-[11px] font-semibold text-gray-500 dark:text-lightBlue/70 uppercase tracking-wider mb-3">
                        {t.apercu}
                    </div>
                    <ApercuDocument document={devis} signature={signature} />

                    <div className="mt-5 flex flex-col sm:flex-row gap-3">
                        <button
                            type="button"
                            onClick={telecharger}
                            className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#D04515] text-white font-semibold transition-opacity duration-200 hover:opacity-90"
                        >
                            <SolarIcon name="download-minimalistic-linear" className="text-lg" />
                            {t.telecharger}
                        </button>
                        <button
                            type="button"
                            onClick={copier}
                            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl border border-gray-200/70 dark:border-white/10 text-gray-600 dark:text-lightBlue font-semibold transition-colors duration-200 hover:border-primary/40 hover:text-primary"
                        >
                            <SolarIcon name={copie ? "check-circle-linear" : "copy-linear"} className="text-lg" />
                            {copie ? t.copie : t.copier}
                        </button>
                        <span role="status" className="sr-only">
                            {copie ? t.copie : ""}
                        </span>
                    </div>

                    <div className="mt-8">
                        <div className="flex items-center gap-2.5 mb-4">
                            <SolarIcon
                                name="clipboard-check-linear"
                                className="text-xl shrink-0 text-gray-400 dark:text-lightBlue/60"
                            />
                            <div className="font-bold dark:text-white leading-snug">{t.suiteTitre}</div>
                        </div>
                        <Liste items={[t.suite1, t.suite2, t.suite3, t.suite4]} />
                    </div>

                    <div className="mt-8 rounded-2xl border border-gray-200/70 dark:border-white/10 bg-white/60 dark:bg-white/[0.03] p-5">
                        <div className="flex items-center gap-2.5 mb-3">
                            <SolarIcon
                                name="bolt-circle-linear"
                                className="text-xl shrink-0 text-gray-400 dark:text-lightBlue/60"
                            />
                            <div className="font-semibold dark:text-white leading-snug">{t.fondTitre}</div>
                        </div>
                        <p className="text-sm leading-7 text-gray-600 dark:text-lightBlue mb-0">{t.fond}</p>
                    </div>

                    <Recommencer onClick={recommencer} libelle={t.recommencer} />
                </div>
            )}
        </Resultat>
    );
}
